import type MainProject from '@/model/MainProject'
import { findResourceFileMetaByStoredPath } from '@/features/virtual-resource/fileNamePath'
import type DataFileMap from '@/model/Resource/DataFileMap'
import {
  applyOfficialFontToResourceRoot,
  findOfficialFontByMd5,
  findOfficialFontByName,
  FONT_DISPLAY_NAME_LINK_INDEX,
  FONT_PREVIEW_MD5_LINK_INDEX,
  getDefaultOfficialFont,
  loadOfficialFontLibrary,
  type OfficialFontItem,
} from '@/features/project-font/library'
import { BrowserFileUtils } from '@/platform/utils/BrowserFileUtils'

export const DEFAULT_PROJECT_FONT_FILE = 'FZZYJW.TTF'
export const DEFAULT_PROJECT_FONT_STORED_PATH = 'FZZYJW'
export const FONT_RESOURCE_BASE_FOLDER = '字体/文件'
const DEFAULT_PROJECT_FONT_NAME = 'FZZYJW'
export const DEFAULT_PROJECT_FONT_DISPLAY_NAME = '方正综艺简体'

const fontLoadTasks = new Map<string, Promise<string | null>>()

export interface ProjectFontFallbackNotice {
  requestedFontName: string
  fallbackFontName: string
  fallbackStoredFontName: string
}

export interface NormalizeProjectFontResourceResult {
  root: DataFileMap | null
  changed: boolean
  fallbackNotice: ProjectFontFallbackNotice | null
}

function sanitizeFontFileName(name: string): string {
  return String(name || '')
    .trim()
    .replace(/[\\/:*?"<>|]/g, '_')
}

function stripFileExtension(name: string): string {
  const cleanName = sanitizeFontFileName(name)
  return cleanName.replace(/\.[^.]+$/i, '')
}

function trimText(value: string): string {
  return String(value || '').replace(/\0/g, '').trim()
}

function normalizeFontStoredPath(value: string): string {
  const text = String(value || '').trim()
  if (!text) return DEFAULT_PROJECT_FONT_STORED_PATH
  if (isDefaultFontRequest(text)) return DEFAULT_PROJECT_FONT_STORED_PATH
  return text.replace(/^\/+|\/+$/g, '')
}

function getFontFamilyFromStoredPath(storedPath: string): string {
  const normalized = normalizeFontStoredPath(storedPath)
  const fileName = normalized.split('/').pop() || normalized
  return stripFileExtension(fileName) || DEFAULT_PROJECT_FONT_NAME
}

function resolvePreferredFontDisplayName(params: {
  storedPath: string
  fileName?: string
  displayName?: string
}): string {
  const normalizedStoredPath = normalizeFontStoredPath(params.storedPath)
  const rawDisplayName = String(params.displayName || '').trim()
  const fileName = String(params.fileName || normalizedStoredPath.split('/').pop() || '').trim()

  // 默认字体始终固定展示中文名，避免被旧数据里的 FZZYJW / FZZYJW.TTF 覆盖。
  if (
    isDefaultFontRequest(normalizedStoredPath)
    || (!!rawDisplayName && isDefaultFontRequest(rawDisplayName))
    || (!!fileName && isDefaultFontRequest(fileName))
  ) {
    return DEFAULT_PROJECT_FONT_DISPLAY_NAME
  }

  if (rawDisplayName) return rawDisplayName
  if (fileName) return stripFileExtension(fileName) || fileName
  return stripFileExtension(normalizedStoredPath) || normalizedStoredPath
}

function readUint16(view: DataView, offset: number): number {
  return view.getUint16(offset, false)
}

function readUint32(view: DataView, offset: number): number {
  return view.getUint32(offset, false)
}

function decodeUtf16Be(bytes: Uint8Array): string {
  const buffer = new Uint8Array(bytes.length + (bytes.length % 2)).buffer
  const normalized = new Uint8Array(buffer)
  normalized.set(bytes)
  return trimText(new TextDecoder('utf-16be').decode(normalized))
}

function decodeLatinText(bytes: Uint8Array): string {
  return trimText(new TextDecoder('latin1').decode(bytes))
}

function tryParseSfntDisplayName(bytes: Uint8Array): string {
  if (bytes.length < 12) return ''
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const signature = String.fromCharCode(...bytes.slice(0, 4))
  const isSfnt =
    signature === 'OTTO' ||
    signature === 'true' ||
    signature === 'typ1' ||
    (bytes[0] === 0x00 && bytes[1] === 0x01 && bytes[2] === 0x00 && bytes[3] === 0x00)
  if (!isSfnt) return ''

  const numTables = readUint16(view, 4)
  let nameOffset = 0
  let nameLength = 0
  for (let index = 0; index < numTables; index += 1) {
    const entryOffset = 12 + index * 16
    if (entryOffset + 16 > bytes.length) break
    const tag = String.fromCharCode(...bytes.slice(entryOffset, entryOffset + 4))
    if (tag !== 'name') continue
    nameOffset = readUint32(view, entryOffset + 8)
    nameLength = readUint32(view, entryOffset + 12)
    break
  }
  if (!nameOffset || !nameLength || nameOffset + nameLength > bytes.length || nameLength < 6) return ''

  const nameView = new DataView(bytes.buffer, bytes.byteOffset + nameOffset, nameLength)
  const count = readUint16(nameView, 2)
  const stringOffset = readUint16(nameView, 4)
  const recordsOffset = nameOffset + 6
  const zhPreferred: string[] = []
  const enPreferred: string[] = []
  const otherPreferred: string[] = []
  const zhFamily: string[] = []
  const enFamily: string[] = []
  const otherFamily: string[] = []

  for (let index = 0; index < count; index += 1) {
    const recordOffset = recordsOffset + index * 12
    if (recordOffset + 12 > nameOffset + nameLength) break
    const platformId = readUint16(view, recordOffset)
    const languageId = readUint16(view, recordOffset + 4)
    const nameId = readUint16(view, recordOffset + 6)
    if (nameId !== 1 && nameId !== 16) continue

    const length = readUint16(view, recordOffset + 8)
    const offset = readUint16(view, recordOffset + 10)
    const textStart = nameOffset + stringOffset + offset
    const textEnd = textStart + length
    if (textStart < 0 || textEnd > nameOffset + nameLength || textEnd > bytes.length) continue
    const textBytes = bytes.slice(textStart, textEnd)
    const decoded = platformId === 0 || platformId === 3 ? decodeUtf16Be(textBytes) : decodeLatinText(textBytes)
    if (!decoded) continue
    const isZh = /^zh(?:-|_|$)/i.test(decoded) || languageId === 0x0804 || languageId === 0x0404 || languageId === 0x0c04 || /[\u4e00-\u9fff]/.test(decoded)
    const isEn = languageId === 0x0409 || /^[\x00-\x7f]+$/.test(decoded)
    const target = nameId === 16
      ? isZh ? zhPreferred : isEn ? enPreferred : otherPreferred
      : isZh ? zhFamily : isEn ? enFamily : otherFamily
    if (!target.includes(decoded)) target.push(decoded)
  }

  return zhPreferred[0] || zhFamily[0] || enPreferred[0] || enFamily[0] || otherPreferred[0] || otherFamily[0] || ''
}

export async function resolveFontDisplayNameFromAsset(fileName: string, blob: Blob): Promise<string> {
  try {
    const bytes = new Uint8Array(await BrowserFileUtils.blobToArrayBuffer(blob))
    const parsed = tryParseSfntDisplayName(bytes)
    if (parsed) return parsed
  } catch {
  }
  if (stripFileExtension(fileName).toUpperCase() === DEFAULT_PROJECT_FONT_NAME) {
    return DEFAULT_PROJECT_FONT_DISPLAY_NAME
  }
  return stripFileExtension(fileName)
}

function getProjectGuid(project: MainProject | null | undefined): string {
  return project?.projectHeader?.ID?.trim() ?? ''
}

function getRequestedFontName(project: MainProject | null | undefined): string {
  const raw = project?.systemData?.totalSetting?.FontName ?? ''
  return String(raw || '').trim()
}

/**
 * 写入工程总设字体名称。
 * @param project 当前工程。
 * @param fontName 要保存到总设中的字体名称。
 */
export function setProjectFontName(project: MainProject, fontName: string) {
  const cleanName = normalizeFontStoredPath(fontName)
  project.systemData.totalSetting.FontName = cleanName
}

function isDefaultFontRequest(fontName: string): boolean {
  const raw = String(fontName || '').trim()
  if (!raw) return true
  if (raw === DEFAULT_PROJECT_FONT_DISPLAY_NAME) return true
  return stripFileExtension(raw).toUpperCase() === DEFAULT_PROJECT_FONT_NAME
}

export function isDefaultProjectFontName(fontName: string): boolean {
  return isDefaultFontRequest(fontName)
}

async function fetchDefaultProjectFontBlob(): Promise<Blob> {
  const defaultFont = getDefaultOfficialFont(await loadOfficialFontLibrary())
  return await fetchBlobFromUrl(defaultFont.resourceUrl)
}

async function resolveProjectFontAsset(fontName: string): Promise<{ item: OfficialFontItem; fileName: string; blob: Blob }> {
  const library = await loadOfficialFontLibrary()
  const item = findOfficialFontByName(library, fontName) ?? getDefaultOfficialFont(library)
  return {
    item,
    fileName: item.fileName,
    blob: await fetchBlobFromUrl(item.resourceUrl),
  }
}

/**
 * 下载官方字体文件并校验响应可用。
 * @param item 官方字体条目。
 * @returns 字体文件 Blob。
 */
export async function downloadOfficialFontBlob(item: OfficialFontItem): Promise<Blob> {
  if (!item.resourceUrl.trim()) {
    throw new Error(`字体资源地址为空：${item.displayName || item.name}`)
  }
  const blob = await fetchBlobFromUrl(item.resourceUrl)
  if (blob.size <= 0) {
    throw new Error(`字体资源为空：${item.displayName || item.name}`)
  }
  return blob
}

export async function createProjectFontObjectUrl(project: MainProject | null): Promise<{
  storedPath: string
  fileName: string
  objectUrl: string
} | null> {
  if (!project || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    return null
  }
  const fontName = await ensureProjectDefaultFont(project)
  const asset = await resolveProjectFontAsset(fontName)
  return {
    storedPath: normalizeFontStoredPath(asset.item.name),
    fileName: asset.fileName,
    objectUrl: URL.createObjectURL(asset.blob),
  }
}

async function loadFontFace(family: string, blob: Blob): Promise<string> {
  const fontUrl = URL.createObjectURL(blob)
  try {
    const fontFace = new FontFace(family, `url(${fontUrl})`)
    await fontFace.load()
    document.fonts.add(fontFace)
    await document.fonts.load(`12px "${family}"`)
    return family
  } finally {
    URL.revokeObjectURL(fontUrl)
  }
}

async function fetchBlobFromUrl(url: string): Promise<Blob> {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`字体资源读取失败：HTTP ${response.status}`)
  }
  return await response.blob()
}

/**
 * 读取工程字体 Blob。
 * @param resourceUrl 字体资源 URL。
 * @returns 字体文件 Blob。
 */
export async function fetchProjectFontBlobFromUrl(resourceUrl: string): Promise<Blob> {
  return await fetchBlobFromUrl(resourceUrl)
}

async function fetchFontBlob(meta: NonNullable<ReturnType<typeof findResourceFileMetaByStoredPath>>): Promise<Blob> {
  return await fetchProjectFontBlobFromUrl(meta.resourceUrl)
}

export function resolveFontDisplayNameFromStoredPath(
  root: DataFileMap | null | undefined,
  storedPath: string,
  baseFolderPath = FONT_RESOURCE_BASE_FOLDER,
): string {
  const normalized = normalizeFontStoredPath(storedPath)
  const meta = findResourceFileMetaByStoredPath(root, 'font', normalized, baseFolderPath)
  const matchedNode = meta?.node ?? findFontResourceNodeByName(root ?? null, normalized)
  return resolvePreferredFontDisplayName({
    storedPath: normalized,
    fileName: matchedNode?.name || normalized.split('/').pop() || normalized,
    displayName: meta?.displayName || String(matchedNode?.links[FONT_DISPLAY_NAME_LINK_INDEX] || ''),
  })
}

export async function ensureFontLoadedFromVirtualResource(params: {
  root: DataFileMap | null | undefined
  storedPath: string
  baseFolderPath?: string
}): Promise<{ family: string; displayName: string; storedPath: string }> {
  const normalized = normalizeFontStoredPath(params.storedPath)
  const meta = findResourceFileMetaByStoredPath(
    params.root,
    'font',
    normalized,
    params.baseFolderPath ?? FONT_RESOURCE_BASE_FOLDER,
  )
  const matchedNode = meta?.node ?? findFontResourceNodeByName(params.root ?? null, normalized)
  const resourceUrl = meta?.resourceUrl || matchedNode?.resourceUrl || ''
  if (resourceUrl) {
    try {
      const blob = await fetchProjectFontBlobFromUrl(resourceUrl)
      const fileName = meta?.name || matchedNode?.name || normalized
      const parsedDisplayName = await resolveFontDisplayNameFromAsset(fileName, blob)
      const displayName = resolvePreferredFontDisplayName({
        storedPath: normalized,
        fileName,
        displayName: meta?.displayName || String(matchedNode?.links[FONT_DISPLAY_NAME_LINK_INDEX] || '') || parsedDisplayName,
      })
      const family = getFontFamilyFromStoredPath(meta?.relativePath || matchedNode?.relativePath || normalized)
      return {
        family: await loadFontFace(family, blob),
        displayName,
        storedPath: meta?.relativePath || matchedNode?.relativePath || normalized,
      }
    } catch {
    }
  }

  const fallbackBlob = await fetchDefaultProjectFontBlob()
  return {
    family: await loadFontFace(DEFAULT_PROJECT_FONT_NAME, fallbackBlob),
    displayName: DEFAULT_PROJECT_FONT_DISPLAY_NAME,
    storedPath: DEFAULT_PROJECT_FONT_STORED_PATH,
  }
}

/**
 * 确保工程总设始终有可用字体名；为空时使用官方默认字体。
 */
export async function ensureProjectDefaultFont(project: MainProject | null, forceDefault = false): Promise<string> {
  if (!project) return DEFAULT_PROJECT_FONT_NAME
  const requestedFontName = getRequestedFontName(project)
  const defaultFont = getDefaultOfficialFont(await loadOfficialFontLibrary())
  if (!forceDefault && requestedFontName.trim() && !isDefaultFontRequest(requestedFontName)) {
    return normalizeFontStoredPath(requestedFontName)
  }
  setProjectFontName(project, defaultFont.name)
  return defaultFont.name
}

export async function resolveProjectFontDisplayName(project: MainProject | null): Promise<string> {
  if (!project) return DEFAULT_PROJECT_FONT_DISPLAY_NAME
  const fontName = await ensureProjectDefaultFont(project)
  const asset = await resolveProjectFontAsset(fontName)
  return await resolveFontDisplayNameFromAsset(asset.fileName, asset.blob)
}

function normalizeFontLookupName(value: string): string {
  const text = String(value || '').trim()
  if (!text) return ''
  const fileName = text.replace(/\\/g, '/').split('/').filter(Boolean).pop() || text
  return stripFileExtension(fileName).toLowerCase()
}

function getFontResourceSignature(node: DataFileMap): string {
  const md5 = node.resourceMd5
  if (md5) return `md5:${md5}`
  const displayName = normalizeFontLookupName(String(node.links[FONT_DISPLAY_NAME_LINK_INDEX] || ''))
  if (displayName) return `display:${displayName}`
  return `name:${normalizeFontLookupName(node.name)}`
}

function collectFontFileNodes(root: DataFileMap | null): DataFileMap[] {
  if (!root) return []
  const nodes: DataFileMap[] = []
  const stack: DataFileMap[] = [root]
  while (stack.length) {
    const node = stack.pop()!
    if (node.isFolder) {
      for (let index = node.children.length - 1; index >= 0; index -= 1) {
        stack.push(node.children[index]!)
      }
      continue
    }
    if (/\.(ttf|otf|woff2?)$/i.test(node.name)) {
      nodes.push(node)
    }
  }
  return nodes
}

function findFontResourceNodeByName(root: DataFileMap | null, fontName: string): DataFileMap | null {
  const target = String(fontName || '').trim()
  if (!root || !target) return null
  const targetLookup = normalizeFontLookupName(target)
  const targetBase = stripFileExtension(target)
  const stack: DataFileMap[] = [root]
  while (stack.length) {
    const node = stack.pop()!
    if (node.isFolder) {
      for (let index = node.children.length - 1; index >= 0; index -= 1) {
        stack.push(node.children[index]!)
      }
      continue
    }
    const displayName = String(node.links[FONT_DISPLAY_NAME_LINK_INDEX] || '').trim()
    const candidateNames = [
      displayName,
      node.name,
      node.relativePath,
      node.mappingPath ? `${node.mappingPath}/${node.relativePath || node.name}` : '',
    ]
    if (
      displayName === target
      || stripFileExtension(node.name) === targetBase
      || candidateNames.some((name) => normalizeFontLookupName(name) === targetLookup)
    ) {
      return node
    }
  }
  return null
}

/**
 * 按字体名、显示名或文件名在虚拟字体资源树中查找可用字体节点。
 * @param root 字体虚拟资源树。
 * @param fontName 工程总设中的字体名称。
 * @returns 命中的字体文件节点；未命中返回 null。
 */
export function findProjectFontResourceNode(root: DataFileMap | null, fontName: string): DataFileMap | null {
  return findFontResourceNodeByName(root, fontName)
}

/**
 * 检查虚拟字体资源树中是否存在并可下载当前工程请求的字体。
 * @param root 字体虚拟资源树。
 * @param fontName 工程总设中的字体名称。
 * @returns true 表示资源树中已有可用字体文件。
 */
export async function hasUsableProjectFontResource(root: DataFileMap | null, fontName: string): Promise<boolean> {
  const node = findProjectFontResourceNode(root, fontName)
  if (!node?.resourceUrl) return false
  try {
    const blob = await fetchProjectFontBlobFromUrl(node.resourceUrl)
    return blob.size > 0
  } catch {
    return false
  }
}

/**
 * 去除虚拟字体资源树中的重复字体文件，避免 Map.bin 字体和官方兜底字体重复展示。
 * @param root 字体虚拟资源树。
 * @returns 原资源树；会就地删除重复文件节点。
 */
export function dedupeProjectFontResources(root: DataFileMap | null): DataFileMap | null {
  if (!root) return root
  const seen = new Set<string>()
  function walk(folder: DataFileMap): void {
    const nextChildren: DataFileMap[] = []
    for (const child of folder.children) {
      if (child.isFolder) {
        walk(child)
        nextChildren.push(child)
        continue
      }
      const signature = getFontResourceSignature(child)
      if (signature && seen.has(signature)) continue
      if (signature) seen.add(signature)
      nextChildren.push(child)
    }
    folder.children = nextChildren
  }
  walk(root)
  return root
}

/**
 * 统一整理工程字体资源树：补齐字体显示名 / 官方预览图，缺失设置字体时补默认字体并改总设。
 * @param project 当前工程。
 * @param root 字体资源树。
 * @returns 整理后的资源树、是否变更、以及是否需要提示用户切换默认字体。
 */
export async function normalizeProjectFontResources(
  project: MainProject,
  root: DataFileMap | null,
): Promise<NormalizeProjectFontResourceResult> {
  const beforeDedupeCount = collectFontFileNodes(root).length
  let nextRoot = dedupeProjectFontResources(root)
  let changed = collectFontFileNodes(nextRoot).length !== beforeDedupeCount
  const library = await loadOfficialFontLibrary()
  const nodes = collectFontFileNodes(nextRoot)

  for (const node of nodes) {
    const officialFont = findOfficialFontByMd5(library, node.resourceMd5)
    if (officialFont) {
      if (node.links[FONT_DISPLAY_NAME_LINK_INDEX] !== officialFont.displayName) {
        node.links[FONT_DISPLAY_NAME_LINK_INDEX] = officialFont.displayName
        changed = true
      }
      if (node.links[FONT_PREVIEW_MD5_LINK_INDEX] !== officialFont.previewMd5) {
        node.links[FONT_PREVIEW_MD5_LINK_INDEX] = officialFont.previewMd5
        changed = true
      }
      continue
    }
    if (node.links[FONT_DISPLAY_NAME_LINK_INDEX]?.trim() || !node.resourceUrl) continue
    try {
      const blob = await fetchProjectFontBlobFromUrl(node.resourceUrl)
      const displayName = (await resolveFontDisplayNameFromAsset(node.name, blob)).trim()
      if (displayName) {
        node.links[FONT_DISPLAY_NAME_LINK_INDEX] = displayName
        changed = true
      }
    } catch {
    }
  }

  const requestedFontName = getRequestedFontName(project)
  if (findProjectFontResourceNode(nextRoot, requestedFontName)) {
    return { root: nextRoot, changed, fallbackNotice: null }
  }

  const defaultFont = getDefaultOfficialFont(library)
  const fallbackNotice = requestedFontName && !isDefaultFontRequest(requestedFontName)
    ? {
        requestedFontName,
        fallbackFontName: defaultFont.displayName,
        fallbackStoredFontName: defaultFont.name,
      }
    : null
  setProjectFontName(project, defaultFont.name)
  const mapped = applyOfficialFontToResourceRoot({
    root: nextRoot,
    item: defaultFont,
  })
  nextRoot = mapped.root
  changed = true
  return { root: nextRoot, changed, fallbackNotice }
}

/**
 * 按当前工程总设把字体加载到浏览器内存，供设置预览和运行时界面复用。
 */
export async function ensureProjectFontLoaded(
  project: MainProject | null,
  root?: DataFileMap | null,
): Promise<string | null> {
  if (!project || typeof FontFace === 'undefined' || typeof document === 'undefined') return null

  const fontName = await ensureProjectDefaultFont(project)
  const guid = getProjectGuid(project) || 'local'
  const rootNode = root ? findFontResourceNodeByName(root, fontName) : null
  const taskKey = `${guid}:${fontName}:${rootNode?.resourceUrl || ''}`
  if (!fontLoadTasks.has(taskKey)) {
    fontLoadTasks.set(
      taskKey,
      (async () => {
        try {
          if (root) {
            return (await ensureFontLoadedFromVirtualResource({
              root,
              storedPath: fontName,
            })).family
          }
          const asset = await resolveProjectFontAsset(fontName)
          return await loadFontFace(getFontFamilyFromStoredPath(fontName), asset.blob)
        } catch {
          setProjectFontName(project, DEFAULT_PROJECT_FONT_NAME)
          const fallbackBlob = await fetchDefaultProjectFontBlob()
          return await loadFontFace(DEFAULT_PROJECT_FONT_NAME, fallbackBlob)
        }
      })().catch((error) => {
        fontLoadTasks.delete(taskKey)
        throw error
      }),
    )
  }
  return await fontLoadTasks.get(taskKey)!
}

